import 'css/src/data/transcript/nodes/SilenceNode.module.scss';

import classNames from 'classnames';
import type { EditorConfig, LexicalNode } from 'lexical';

import type {
    CreateTranscriptWordNodeOptions,
    SerializedTranscriptWordNode,
    SilenceNodeType,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import {
    $isTranscriptWordNode,
    SilenceNodeTypeValue,
    TranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';

export const SILENCE_TEXT_CONTENT = ' • ';
// The transcript word text is different from the DOM text content because text processing sometimes removes leading and tailing spaces
export const SILENCE_TRANSCRIPT_WORD_TEXT_CONTENT = '•';

export type SerializedSilenceNode = SerializedTranscriptWordNode & {
    type: SilenceNodeType;
};

export type CreateSilenceNodeOptions = CreateTranscriptWordNodeOptions & {
    isTemp?: boolean;
};

export class SilenceNode extends TranscriptWordNode {
    // Determines if the silence node is temporary and still being modified
    __isTemp: boolean = false;

    constructor(
        text: string,
        startTime: number,
        endTime: number,
        { key, id, isTemp }: CreateSilenceNodeOptions = {},
    ) {
        super(text, startTime, endTime, { key, id });
        this.__isTemp = isTemp ?? false;
    }

    static override getType(): string {
        return SilenceNodeTypeValue;
    }

    static override clone(node: SilenceNode): SilenceNode {
        return new SilenceNode(node.__text, node.__startTime, node.__endTime, {
            key: node.__key,
            id: node.__id,
            isTemp: node.__isTemp,
        });
    }

    override createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        element.className = classNames(element.className, 'silence-node');
        const dataTestId = this.isTemp ? 'silence-node-temporary' : 'silence-node';
        element.setAttribute('data-testid', dataTestId);
        return element;
    }

    static override importJSON(serializedNode: SerializedSilenceNode): SilenceNode {
        const node = $createSilenceNode(serializedNode.startTime, serializedNode.endTime);
        return node;
    }

    override exportJSON(): SerializedSilenceNode {
        return {
            ...super.exportJSON(),
            type: SilenceNodeTypeValue,
        };
    }

    override remove(preserveEmptyParent?: boolean): void {
        if (this.isTemp) {
            super.remove(preserveEmptyParent);
            return;
        }
        return undefined;
    }

    delete(): void {
        const self = super.getWritable();
        self.__isTemp = true;
        self.remove();
    }

    override updateDOM(
        prevNode: TranscriptWordNode,
        dom: HTMLElement,
        config: EditorConfig,
    ): boolean {
        super.updateDOM(prevNode, dom, config);
        this.setTextContent(SILENCE_TEXT_CONTENT);
        dom.textContent = SILENCE_TEXT_CONTENT;
        return false;
    }

    override canInsertTextBefore(): boolean {
        return false;
    }

    override canInsertTextAfter(): boolean {
        return false;
    }

    get isTemp() {
        return this.__isTemp;
    }

    get transcriptWordTextContent(): string {
        return SILENCE_TRANSCRIPT_WORD_TEXT_CONTENT;
    }
}

export function $isSilenceNode(node: LexicalNode | undefined | null): node is SilenceNode {
    return node instanceof SilenceNode;
}
export function $isTempSilenceNode(node: LexicalNode | undefined | null): node is SilenceNode {
    return node instanceof SilenceNode && node.isTemp;
}

export function $createSilenceNode(
    startTime: number,
    endTime: number,
    { key, id, isTemp }: CreateSilenceNodeOptions = {},
): SilenceNode {
    return new SilenceNode(SILENCE_TEXT_CONTENT, startTime, endTime, {
        key: undefined,
        id,
        isTemp,
    });
}

export function $isTranscriptWordNodeAndNotSilence(
    node: LexicalNode | undefined | null,
): node is TranscriptWordNode {
    return $isTranscriptWordNode(node) && !$isSilenceNode(node);
}
